import { useMemo } from 'react'
import BreachNotificationPanel from './BreachNotificationPanel'
import { getOrgCharacteristics, getFrameworkOverlays, buildFullRegulatoryContext } from '../../../shared/utils/orgCharacteristics'
import { resolveThreatProfile } from '../../../shared/utils/resolveThreatProfile'

/**
 * Extract client's primary state from business scoping responses.
 * Duplicated from ThreatContextStep — shared extraction logic.
 */
function extractClientState(businessScoping, client) {
  const hqStateResponse = businessScoping?.['sl-1']
  if (hqStateResponse) {
    const value = typeof hqStateResponse === 'object' ? hqStateResponse.value : hqStateResponse
    if (value && value !== '') return value
  }
  if (client?.state) return client.state
  if (client?.address) {
    const stateMatch = client.address.match(/,\s*([A-Z]{2})\s*\d{5}/)
    if (stateMatch) return stateMatch[1]
  }
  return null
}

function RegulatoryContextSection({ responses, client, hideBreachResponse = false, breachResponseOnly = false }) {
  // Resolve threat profile via shared cascade so regulatory context derives
  // from the client's industry/sub-industry automatically.
  const selectedProfile = useMemo(() => {
    const { profile } = resolveThreatProfile({ client, responses })
    return profile
  }, [client, responses])

  const clientState = useMemo(() =>
    extractClientState(responses?.businessScoping, client),
    [responses?.businessScoping, client]
  )

  const orgCharacteristics = useMemo(() =>
    getOrgCharacteristics(responses?.businessScoping),
    [responses?.businessScoping]
  )

  const frameworkOverlays = useMemo(() =>
    getFrameworkOverlays(responses?.businessScoping),
    [responses?.businessScoping]
  )

  const displayRegulatoryContext = useMemo(() => {
    if (!selectedProfile?.regulatoryContext) return null
    return buildFullRegulatoryContext(
      selectedProfile.regulatoryContext,
      orgCharacteristics,
      frameworkOverlays
    )
  }, [selectedProfile, orgCharacteristics, frameworkOverlays])

  if (!selectedProfile) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <p className="text-gray-500 dark:text-gray-400 mb-2">No regulatory context available</p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Complete the Threat Context step to generate regulatory context data.
        </p>
      </div>
    )
  }

  const hasOrgOverlays = orgCharacteristics.hasCharacteristics && orgCharacteristics.modifiers?.regulatoryOverlays?.length > 0
  const hasFrameworkOverlays = frameworkOverlays.hasOverlays && frameworkOverlays.modifiers?.narrativeFrames?.length > 0

  return (
    <div className={breachResponseOnly ? 'space-y-6' : 'card p-6'}>
      {/* Primary Header — hidden in breachResponseOnly mode */}
      {!breachResponseOnly && (
      <>
      <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Organizational Regulatory Context
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          Regulatory landscape for <span className="font-medium text-gray-600 dark:text-gray-300">{selectedProfile.verticalName}</span> organizations based on industry profile, organizational characteristics, and compliance selections
        </p>
      </div>

      {/* Organizational Characteristic Overlays */}
      {hasOrgOverlays && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
            Organizational Regulatory Overlays
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Additional requirements from: {orgCharacteristics.selectedLabels.join(', ')}
          </p>
          <div className="flex flex-wrap gap-2">
            {orgCharacteristics.modifiers.regulatoryOverlays.map((overlay, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs rounded-full"
              >
                {overlay}
              </span>
            ))}
          </div>
          {orgCharacteristics.modifiers?.narrativeFrames?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {orgCharacteristics.modifiers.narrativeFrames.map((frame, idx) => (
                <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {frame}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compliance Framework Context */}
      {hasFrameworkOverlays && (
        <div className={hasOrgOverlays ? 'mb-5' : 'mb-5'}>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
            Compliance Framework Context
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            From selected frameworks: {frameworkOverlays.selectedLabels.join(', ')}
          </p>
          <div className="space-y-1.5">
            {frameworkOverlays.modifiers.narrativeFrames.map((frame, idx) => (
              <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                {frame}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Divider before base regulatory details */}
      {(hasOrgOverlays || hasFrameworkOverlays) && (
        <div className="border-t border-gray-200 dark:border-gray-700 my-5" />
      )}

      {/* Base Regulatory Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Applicable Frameworks</h4>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const baseFrameworks = selectedProfile.regulatoryContext.frameworks || []
              const overlayFrameworks = displayRegulatoryContext?.frameworkOverlays?.regulatoryFrameworks || []
              // Normalize for comparison (lowercase, strip version suffixes)
              const normalize = (fw) => fw.toLowerCase().replace(/\s*v[\d.]+$/, '').trim()
              const baseNormalized = new Set(baseFrameworks.map(normalize))
              // Only show overlay frameworks that aren't already in the base set
              const uniqueOverlays = overlayFrameworks.filter(fw => !baseNormalized.has(normalize(fw)))

              return (
                <>
                  {baseFrameworks.map((framework, idx) => (
                    <span
                      key={`base-${idx}`}
                      className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs rounded-full"
                    >
                      {framework}
                    </span>
                  ))}
                  {uniqueOverlays.map((framework, idx) => (
                    <span
                      key={`overlay-${idx}`}
                      className="px-2 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs rounded-full"
                      title="Added from Business Scoping selections"
                    >
                      {framework}
                    </span>
                  ))}
                </>
              )
            })()}
          </div>
          {displayRegulatoryContext?.frameworkOverlays?.selectedFrameworks?.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              <span className="inline-block w-2 h-2 bg-purple-400 rounded-full mr-1"></span>
              Added based on your compliance requirements selection
            </p>
          )}
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 flex items-center">
            Maximum Penalty
            {selectedProfile.regulatoryContext.maxPenaltySource && (
              <span className="relative ml-1.5 group">
                <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="absolute z-50 left-0 bottom-full mb-2 w-48 p-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <span className="font-medium">Source:</span> {selectedProfile.regulatoryContext.maxPenaltySource}
                </span>
              </span>
            )}
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Up to ${selectedProfile.regulatoryContext.maxPenalty.toLocaleString()}
          </p>
        </div>
      </div>
      </>
      )}

      {/* Breach Response sections — always shown unless hideBreachResponse */}
      {/* Breach Notification summary */}
      {!hideBreachResponse && (
        <div className="card p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Breach Notification</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedProfile.regulatoryContext.breachNotificationDays} days notification requirement
              </p>
              {selectedProfile.regulatoryContext.breachNotification?.primaryAuthority && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 font-mono leading-relaxed">
                  {selectedProfile.regulatoryContext.breachNotification.primaryAuthority.citation}
                </p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reporting Requirements</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
                {(() => {
                  const baseReqs = selectedProfile.regulatoryContext.reportingRequirements?.slice(0, 5) || []
                  const overlayAuths = displayRegulatoryContext?.breachNotification?.additionalAuthorities || []

                  // Deduplicate: check if overlay authority is already covered by a base requirement
                  const DEDUP_KEYWORDS = {
                    'occ': ['occ', 'comptroller'],
                    'fdic': ['fdic', 'federal deposit'],
                    'ncua': ['ncua', 'credit union'],
                    'federal regulator': ['occ', 'fdic', 'ncua', 'federal regulator', 'federal financial'],
                    'state banking': ['state banking', 'state regulator'],
                    'pci': ['pci', 'payment card', 'security standards council']
                  }

                  const baseNormalized = baseReqs.map(r => r.toLowerCase())
                  const uniqueOverlays = overlayAuths.filter(auth => {
                    const authLower = auth.name.toLowerCase()
                    // Check if any base requirement covers this overlay
                    return !baseNormalized.some(base => {
                      // Direct substring match
                      if (base.includes(authLower) || authLower.includes(base)) return true
                      // Keyword overlap check
                      for (const keywords of Object.values(DEDUP_KEYWORDS)) {
                        const authMatches = keywords.some(k => authLower.includes(k))
                        const baseMatches = keywords.some(k => base.includes(k))
                        if (authMatches && baseMatches) return true
                      }
                      return false
                    })
                  })

                  return (
                    <>
                      {baseReqs.map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                      {uniqueOverlays.map((auth, idx) => (
                        <li key={`overlay-${idx}`} className="text-purple-600 dark:text-purple-400">
                          {auth.name}
                          <span className="text-xs ml-1">(from assessment)</span>
                        </li>
                      ))}
                    </>
                  )
                })()}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Breach Notification Panel */}
      {!hideBreachResponse && (
        <BreachNotificationPanel
          profile={selectedProfile}
          clientState={clientState}
          enhancedRegulatoryContext={displayRegulatoryContext}
        />
      )}
    </div>
  )
}

export default RegulatoryContextSection
